import { pageKeyHints } from "../touch/key-hints.ts";
import { FRAME_LIMITS, frameLimitOffered, type FrameLimit } from "odyssey-shared";
import { say } from "../../words.ts";

/**
 * The frame limit as Settings offers it (ADR-093 decisions 40 and 43): 30, 60, 120 and Match display, 120
 * disabled unless the display is measured faster than 100 Hz, and the row's note. A choice's value is its text as
 * `settings.ts` keeps it in the store. Every word is a draft for the author.
 */

/**
 * One choice as the segmented choice takes it.
 */
export interface FrameOption {
  readonly value: FrameLimit;
  readonly label: string;
  readonly disabled: boolean;
}

/**
 * A choice's label.
 *
 * @param limit The choice.
 * @returns Its words.
 */
function label(limit: FrameLimit): string {
  switch (limit) {
    case "30":
      return say("settings.frameLimitChoices.fps30");
    case "60":
      return say("settings.frameLimitChoices.fps60");
    case "120":
      return say("settings.frameLimitChoices.fps120");
    default:
      return say("settings.frameLimitChoices.match");
  }
}

/**
 * The choices in the order 30, 60, 120, Match display.
 *
 * @param refresh The display's measured rate in Hz, or null before it is measured.
 * @returns Each choice's value and label, 120 disabled unless the display is measured faster than 100 Hz.
 * @example
 * frameOptions(60).map((option) => option.disabled);
 * // [false, false, true, false]
 */
export function frameOptions(refresh: number | null): readonly FrameOption[] {
  return FRAME_LIMITS.map((value) => ({ value, label: label(value), disabled: !frameLimitOffered(value, refresh) }));
}

/**
 * The row's note, under the row: what 30 costs when it is the pending choice, and why 120 is disabled while it is.
 *
 * @param pending The pending choice.
 * @param refresh The display's measured rate in Hz, or null before it is measured.
 * @param keyed Whether a key may be named (`touch/key-hints.ts`); without one the note speaks of a tap.
 * @returns The sentences joined by a space, or null when there is nothing to say.
 */
export function frameNote(pending: FrameLimit, refresh: number | null, keyed: boolean = pageKeyHints.getSnapshot()): string | null {
  const words: string[] = [];
  if (pending === "30") {
    words.push(say(keyed ? "settings.frameLimitNotes.thirty" : "settings.frameLimitNotes.thirtyTouch"));
  }
  if (!frameLimitOffered("120", refresh)) {
    words.push(refresh === null ? say("settings.frameLimitNotes.unmeasured") : say("settings.frameLimitNotes.slow", { hz: refresh }));
  }
  return words.length === 0 ? null : words.join(" ");
}
